import type { TextFacet } from "../structs.ts";

export interface MentionCandidate {
  readonly start: number;
  readonly end: number;
  readonly handle: string;
}

export interface TagCandidate {
  readonly start: number;
  readonly end: number;
  readonly tag: string;
}

export interface LinkCandidate {
  readonly start: number;
  readonly end: number;
  readonly uri: string;
}

interface Range {
  readonly start: number;
  readonly end: number;
}

function overlaps(ranges: readonly Range[], start: number, end: number): boolean {
  return ranges.some((range) => start < range.end && range.start < end);
}

const mentionPattern =
  /(^|[^A-Za-z0-9_@])@([A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+)/g;

export function extractMentions(text: string, occupied: readonly Range[]): MentionCandidate[] {
  const found: MentionCandidate[] = [];
  for (const match of text.matchAll(mentionPattern)) {
    const prefix = match[1]!;
    const handle = match[2]!;
    const start = match.index + prefix.length;
    const end = start + handle.length + 1;
    if (!overlaps(occupied, start, end)) found.push({ start, end, handle });
  }
  return found;
}

const tagPattern = /(^|[^A-Za-z0-9_#])#([\p{Letter}\p{Number}_][\p{Letter}\p{Number}_.-]{0,63})/gu;

export function extractTags(text: string, occupied: readonly Range[]): TagCandidate[] {
  const found: TagCandidate[] = [];
  for (const match of text.matchAll(tagPattern)) {
    const prefix = match[1]!;
    const tag = match[2]!.replace(/[.-]+$/, "");
    if (!tag || [...tag].length > 64) continue;
    const start = match.index + prefix.length;
    const end = start + tag.length + 1;
    if (!overlaps(occupied, start, end)) found.push({ start, end, tag });
  }
  return found;
}

const linkPattern = /(https?:\/\/[^\s<>"'`\]]+)/g;

function trimLink(raw: string): string {
  let end = raw.length;
  while (end > 0 && ".,;:!?".includes(raw[end - 1]!)) end--;
  let trimmed = raw.slice(0, end);
  for (const [open, close] of [["(", ")"], ["[", "]"]] as const) {
    const opens = trimmed.split(open).length - 1;
    let closes = trimmed.split(close).length - 1;
    while (closes > opens && trimmed.endsWith(close)) {
      trimmed = trimmed.slice(0, -1);
      closes--;
    }
  }
  return trimmed;
}

function safeLink(value: string): string | undefined {
  if (!value || /\s/.test(value)) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : undefined;
  } catch {
    return undefined;
  }
}

export function extractLinks(text: string, occupied: readonly Range[]): LinkCandidate[] {
  const found: LinkCandidate[] = [];
  for (const match of text.matchAll(linkPattern)) {
    const trimmed = trimLink(match[0]);
    const uri = safeLink(trimmed);
    if (!uri) continue;
    const start = match.index;
    const end = start + trimmed.length;
    if (!overlaps(occupied, start, end)) found.push({ start, end, uri });
  }
  return found;
}

/** Add bare-link, resolved-mention, and tag facets around explicit markdown facets. */
export function enrichFacets(
  text: string,
  base: readonly TextFacet[],
  dids: ReadonlyMap<string, `did:${string}` | undefined>,
): TextFacet[] {
  const occupied: Range[] = base.map((facet) => ({ start: facet.start, end: facet.end }));
  const facets: TextFacet[] = [...base];
  for (const link of extractLinks(text, occupied)) {
    occupied.push(link);
    facets.push({
      $type: "blue.cerulea.app.facet#link",
      start: link.start,
      end: link.end,
      uri: link.uri,
    });
  }
  for (const mention of extractMentions(text, occupied)) {
    const did = dids.get(mention.handle.toLowerCase());
    if (!did) continue;
    occupied.push(mention);
    facets.push({
      $type: "blue.cerulea.app.facet#mention",
      start: mention.start,
      end: mention.end,
      did,
    });
  }
  for (const tag of extractTags(text, occupied)) {
    occupied.push(tag);
    facets.push({
      $type: "blue.cerulea.app.facet#tag",
      start: tag.start,
      end: tag.end,
      tag: tag.tag,
    });
  }
  return facets;
}
